/**
 * Der Eingang — alle Unterlagen auf einmal.
 *
 * Eine Ablagefläche für den ganzen Ordner (auch als ZIP). Der Server sagt
 * zu jeder Datei, was sie ist; hier steht die Liste der Vorschläge: was
 * unsicher ist oben, mit Auswahlfeld, was sicher ist darunter. Ein Klick
 * „Übernehmen" stellt alles bereit — vorher sieht der Beirat nichts.
 *
 * Vier Sonderfälle bekommen eigene Entscheidungen: die Buchungsliste wird
 * als Importpaket eingespielt, ein Journal abgeglichen, aus den
 * Einzelabrechnungen die Gesamtabrechnung abgeleitet, und Mieterunterlagen
 * werden zurückgehalten.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '../../lib/api';
import type { EingangAktion, Eingangsposten, Kategorie, Pruefung } from '../../lib/typen';
import { Ablagezone } from '../../komponenten/Ablagezone';
import { groesseText } from '../../komponenten/Dokumentansicht';

const SICHER_AB = 0.7;
const SCHRITT_NAMEN: Record<number, string> = { 1: 'Kontenabgleich', 2: 'Gesamtabrechnung', 3: 'Kontenblätter und Belege', 4: 'Abrechnungsergebnis', 5: 'Wirtschaftsplan' };

interface Wahl {
  aktion: EingangAktion;
  kategorieId: string | null;
}

export function Eingang({ pruefung, onMeldung, onUebernommen }: { pruefung: Pruefung; onMeldung: (t: string) => void; onUebernommen: () => void }) {
  const [posten, setPosten] = useState<Eingangsposten[]>([]);
  const [kategorien, setKategorien] = useState<Kategorie[]>([]);
  const [kiVerfuegbar, setKiVerfuegbar] = useState(false);
  const [wahl, setWahl] = useState<Record<string, Wahl>>({});
  /** Vom Hausverwalter geänderte Anzeigenamen, je Posten. */
  const [namen, setNamen] = useState<Record<string, string>>({});
  const [salden, setSalden] = useState(true);
  const [laeuft, setLaeuft] = useState<'hochladen' | 'uebernehmen' | null>(null);
  const [meldungen, setMeldungen] = useState<string[]>([]);
  const istImport = pruefung.belegquelle === 'import';

  const vorbelegen = useCallback(
    (liste: Eingangsposten[]) => {
      setWahl((alt) => {
        const neu = { ...alt };
        for (const p of liste) {
          if (neu[p.id]) continue;
          const e = p.analyse.erkennung;
          // Die nackte CSV neben dem ZIP, das sie samt Belegen enthält: nur das ZIP einspielen.
          const paketDaneben =
            e.art === 'importpaket' &&
            !p.dateiname.toLowerCase().endsWith('.zip') &&
            liste.some((q) => q.id !== p.id && q.analyse.erkennung.art === 'importpaket' && q.dateiname.toLowerCase().endsWith('.zip') && q.quellePfad === p.quellePfad);
          neu[p.id] =
            paketDaneben
              ? { aktion: 'verwerfen', kategorieId: null }
              : e.art === 'importpaket'
              ? { aktion: 'importpaket', kategorieId: null }
              : e.art === 'journal'
                ? { aktion: 'journal', kategorieId: p.analyse.kategorieId }
                : e.art === 'einzelabrechnungen'
                  ? { aktion: p.analyse.ableitung?.bloecke.length ? 'ableiten' : 'zurueckhalten', kategorieId: null }
                  : e.art === 'mieter'
                    ? { aktion: 'zurueckhalten', kategorieId: null }
                    : { aktion: 'unterlage', kategorieId: p.analyse.kategorieId };
        }
        return neu;
      });
    },
    [],
  );

  const laden = useCallback(async () => {
    try {
      const a = await api.eingang.lesen(pruefung.id);
      setPosten(a.posten);
      setKategorien(a.kategorien.filter((k) => k.aktiv));
      setKiVerfuegbar(a.kiVerfuegbar);
      vorbelegen(a.posten);
    } catch {
      // Ohne Eingang keine Liste — das ist der Normalfall.
    }
  }, [pruefung.id, vorbelegen]);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function hochladen(dateien: File[]) {
    setLaeuft('hochladen');
    setMeldungen([]);
    onMeldung(`${dateien.length} ${dateien.length === 1 ? 'Datei wird' : 'Dateien werden'} gelesen und erkannt …`);
    try {
      const a = await api.eingang.hochladen(pruefung.id, dateien);
      setPosten(a.posten);
      setKategorien(a.kategorien.filter((k) => k.aktiv));
      setKiVerfuegbar(a.kiVerfuegbar);
      vorbelegen(a.posten);
      const unsicher = a.posten.filter((p) => p.analyse.erkennung.sicherheit < SICHER_AB).length;
      onMeldung(`${a.angelegt} Dateien erkannt${unsicher ? ` — ${unsicher} bitte zuordnen` : ''}.`);
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setLaeuft(null);
    }
  }

  async function uebernehmen() {
    setLaeuft('uebernehmen');
    try {
      const entscheidungen = posten.map((p) => ({
        id: p.id,
        ...(wahl[p.id] ?? { aktion: 'unterlage' as const, kategorieId: p.analyse.kategorieId }),
        ...(namen[p.id] !== undefined && namen[p.id].trim() && namen[p.id] !== p.dateiname ? { dateiname: namen[p.id] } : {}),
      }));
      const a = await api.eingang.uebernehmen(pruefung.id, entscheidungen, salden);
      setMeldungen(a.meldungen);
      onMeldung(
        `${a.unterlagen} Unterlagen bereitgestellt${a.zurueckgehalten ? `, ${a.zurueckgehalten} zurückgehalten` : ''}${a.salden ? `, ${a.salden} Salden eingetragen` : ''}${
          a.verbleibend ? ` — ${a.verbleibend} noch im Eingang` : ''
        }.`,
      );
      await laden();
      onUebernommen();
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setLaeuft(null);
    }
  }

  async function leeren() {
    if (!window.confirm('Alle Dateien im Eingang verwerfen?')) return;
    await api.eingang.leeren(pruefung.id);
    setPosten([]);
    setWahl({});
    setNamen({});
    setMeldungen([]);
  }

  const katNach = useMemo(() => new Map(kategorien.map((k) => [k.id, k])), [kategorien]);
  const unsichere = posten.filter((p) => p.analyse.erkennung.sicherheit < SICHER_AB && !['mieter', 'einzelabrechnungen', 'importpaket'].includes(p.analyse.erkennung.art));
  const sichere = posten.filter((p) => !unsichere.includes(p));
  const offen = posten.filter((p) => {
    const w = wahl[p.id];
    return !w || (w.aktion === 'unterlage' && !w.kategorieId);
  }).length;
  const saldenAnzahl = posten.reduce((n, p) => n + p.analyse.erkennung.salden.length + (p.analyse.ableitung?.geldkonten.length ?? 0), 0);

  function wahlText(w: Wahl | undefined, p: Eingangsposten): string {
    if (!w) return '';
    return `${w.aktion}:${w.kategorieId ?? p.analyse.kategorieId ?? ''}`;
  }

  function wahlSetzen(p: Eingangsposten, wert: string) {
    const [aktion, kategorieId] = wert.split(':');
    setWahl((alt) => ({ ...alt, [p.id]: { aktion: aktion as EingangAktion, kategorieId: kategorieId || null } }));
  }

  function zeile(p: Eingangsposten) {
    const e = p.analyse.erkennung;
    const w = wahl[p.id];
    const ton = e.art === 'mieter' || e.art === 'einzelabrechnungen' ? 'warn' : e.sicherheit >= SICHER_AB ? 'ok' : '';
    return (
      <div className="eingang-zeile" key={p.id}>
        <div className="eingang-datei">
          <input
            className="eingang-name"
            value={namen[p.id] ?? p.dateiname}
            onChange={(ev) => setNamen((alt) => ({ ...alt, [p.id]: ev.target.value }))}
            onBlur={(ev) => {
              if (!ev.target.value.trim()) setNamen((alt) => ({ ...alt, [p.id]: p.dateiname }));
            }}
            aria-label="Anzeigename der Unterlage"
            title="Name, unter dem der Beirat die Unterlage sieht — zum Ändern einfach hineinschreiben"
            spellCheck={false}
          />
          <div className="s" style={{ margin: 0 }}>
            {p.quellePfad ? `${p.quellePfad} · ` : ''}
            {p.seiten ? `${p.seiten} S. · ` : ''}
            {groesseText(p.groesse)}
            {' · '}
            <a href={api.eingang.dateiUrl(pruefung.id, p.id)} target="_blank" rel="noreferrer">
              Ansehen
            </a>
          </div>
        </div>
        <div className="eingang-grund">
          <span className={`chip ${ton}`}>{artText(e.art, e.sicherheit)}</span>
          <div className="s" style={{ margin: '2px 0 0' }}>
            {e.grund}
            {e.quelle === 'ki' ? ' · Vorschlag der KI' : ''}
            {p.analyse.ableitung && p.analyse.ableitung.bloecke.length > 0 && ` · Gesamtabrechnung ableitbar (${p.analyse.ableitung.einzelabrechnungen} Einzelabrechnungen)`}
            {e.salden.length > 0 && ` · ${e.salden.length} Saldo${e.salden.length === 1 ? '' : 's'} gelesen`}
          </div>
        </div>
        <select className="eingang-wahl" value={wahlText(w, p)} onChange={(ev) => wahlSetzen(p, ev.target.value)}>
          <option value="unterlage:" disabled>
            — Menüpunkt wählen —
          </option>
          {[1, 2, 3, 4, 5].map((nr) => (
            <optgroup key={nr} label={`Schritt ${nr} · ${SCHRITT_NAMEN[nr]}`}>
              {kategorien
                .filter((k) => k.schritt === nr)
                .map((k) => (
                  <option key={k.id} value={`unterlage:${k.id}`}>
                    {k.bezeichnung}
                  </option>
                ))}
            </optgroup>
          ))}
          <optgroup label="Besondere Verarbeitung">
            {istImport && <option value="importpaket:">Importpaket einspielen (Belege + Buchungen)</option>}
            {p.typ === 'application/pdf' && <option value={`journal:${katNach.has(p.analyse.kategorieId ?? '') ? p.analyse.kategorieId : ''}`}>Journal abgleichen (Schritt 3)</option>}
            {p.analyse.ableitung && p.analyse.ableitung.bloecke.length > 0 && <option value="ableiten:">Gesamtabrechnung ableiten — Einzelabrechnungen nicht übernehmen</option>}
            <option value="zurueckhalten:">Nicht für den Beirat — zurückhalten</option>
            <option value="verwerfen:">Verwerfen</option>
          </optgroup>
        </select>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 16, marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Alle Unterlagen auf einmal</h3>
        <span className="s" style={{ margin: 0 }}>
          Den ganzen Ordner ablegen — als ZIP oder als einzelne Dateien. Die Anwendung erkennt, was es ist, und sortiert es den Prüfschritten zu.
          {kiVerfuegbar ? ' Unbekanntes wird zusätzlich von der KI eingeordnet.' : ''}
        </span>
      </div>

      <div className="eingang-flaeche" style={{ marginTop: 10 }}>
      <Ablagezone
        endungen={['.pdf', '.zip', '.csv', '.xlsx', '.xls', '.png', '.jpg', '.jpeg', '.html']}
        mehrfach
        gross
        titel={laeuft === 'hochladen' ? 'Wird gelesen und erkannt …' : 'Ordner oder Dateien hier ablegen'}
        hinweis="Ein ZIP des ganzen Ordners (Unterordner sind in Ordnung), eine einzelne Datei oder viele auf einmal — PDF, CSV, Excel, Bilder. Mieterunterlagen werden erkannt und zurückgehalten."
        onDateien={hochladen}
      />
      </div>

      {posten.length > 0 && (
        <div style={{ marginTop: 14 }}>
          {unsichere.length > 0 && (
            <>
              <div className="eingang-kopf">
                <b>Bitte zuordnen</b>
                <span className="badge w">{unsichere.length}</span>
                <span className="s" style={{ margin: 0 }}>Hier war sich die Erkennung nicht sicher.</span>
              </div>
              {unsichere.map((p) => (
                zeile(p)
              ))}
            </>
          )}
          {sichere.length > 0 && (
            <>
              <div className="eingang-kopf" style={{ marginTop: unsichere.length ? 14 : 0 }}>
                <b>Erkannt</b>
                <span className="badge">{sichere.length}</span>
                <span className="s" style={{ margin: 0 }}>Vorschlag prüfen, bei Bedarf ändern.</span>
              </div>
              {sichere.map((p) => (
                zeile(p)
              ))}
            </>
          )}

          <div className="eingang-fuss">
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={salden} onChange={(e) => setSalden(e.target.checked)} />
              Salden in den Kontenabgleich (Schritt 1) eintragen{saldenAnzahl ? ` — ${saldenAnzahl} gelesen` : ''}
            </label>
            <div className="spacer" />
            <button className="btn ghost sm" onClick={leeren} disabled={laeuft !== null}>
              Eingang leeren
            </button>
            <button className="btn primary" onClick={uebernehmen} disabled={laeuft !== null || offen > 0} title={offen ? `${offen} Dateien ohne Menüpunkt` : undefined}>
              {laeuft === 'uebernehmen' ? 'Wird übernommen …' : `Übernehmen (${posten.length})`}
            </button>
          </div>
          {offen > 0 && (
            <p className="note" style={{ color: 'var(--warn)', marginBottom: 0 }}>
              {offen} {offen === 1 ? 'Datei hat' : 'Dateien haben'} noch keinen Menüpunkt — oben auswählen oder „Verwerfen".
            </p>
          )}
        </div>
      )}

      {meldungen.length > 0 && (
        <ul className="eingang-meldungen">
          {meldungen.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function artText(art: Eingangsposten['analyse']['erkennung']['art'], sicherheit: number): string {
  switch (art) {
    case 'unterlage':
      return sicherheit >= SICHER_AB ? 'Unterlage erkannt' : 'Unsicher';
    case 'importpaket':
      return 'Importpaket';
    case 'journal':
      return 'Journal';
    case 'einzelabrechnungen':
      return 'Einzelabrechnungen — nicht für den Beirat';
    case 'mieter':
      return 'Mieterunterlage — nicht für den Beirat';
    default:
      return 'Nicht erkannt';
  }
}
